import type { CSSProperties } from 'react'
import { TOKENS } from '../../ui/dashboard-primitives'

import { CHART_GRID, CHART_PALETTE, SrChartTable } from '../../ui/chart-primitives'
import { useUnits } from '../../../lib/UnitsProvider'
import { ArtifactCard } from './ArtifactCard'
import type { ProgressionArtifactData } from './types'

/**
 * The mockup's geometry, kept as a viewBox so the card scales with the bubble.
 * Transcribed from the approved V3 file (`Coach v3.dc.html`), which is why these
 * are chosen numbers rather than derived ones — the plan's own values disagreed
 * with the file, and the file wins.
 */
const VB_W = 440
const VB_H = 120

/**
 * Asymmetric on purpose. The line starts 10px in but stops 30px short, so the
 * latest value's label and its 4px dot have somewhere to sit without touching
 * the right edge. Padding the two sides equally pushes the label out of frame.
 */
const PAD_LEFT = 10
const PAD_RIGHT = 30

/** The band the data maps into: lowest value on PLOT_BOTTOM, highest on PLOT_TOP. */
const PLOT_TOP = 34
const PLOT_BOTTOM = 92

/** Anchors the chart. Same weight as every other grid line in the app. */
const BASELINE_Y = 100

/**
 * The mid guide — and the line a flat series is drawn on, deliberately the same
 * number. The plot band's own centre is 63; drawing a stalled lift there would
 * put it 8px under the guide and read as a near-miss rather than as "no change".
 */
const MID_Y = 55

/**
 * Fainter than `CHART_GRID.stroke`, which the baseline uses. The two lines are a
 * hierarchy: the baseline anchors the chart, the mid line is a whisper behind
 * the data. `CHART_GRID` has no token this faint, and adding one would change a
 * module shared with every Recharts chart in the app for a single sparkline — so
 * it is local, matching the V3 file's own value.
 */
const MID_STROKE = TOKENS.creamHairline

/**
 * The two value labels are **HTML overlaid on the chart, not SVG `<text>`**, and
 * both track their own data point. Two browser findings forced that, neither of
 * which jsdom or the mockup could show:
 *
 * 1. **They must not scale with the viewBox.** At a 390px viewport the chart
 *    renders 232px wide — a 0.53 scale — and `font-size="10"` measured 7px tall
 *    and 9.5px wide. Illegible. A CSS px size holds at every width, while the
 *    line, grid and dot are geometry and scale correctly.
 * 2. **A fixed footer position mislabels the series.** The V3 file pins the
 *    opening value at `y=112`, under the baseline, which reads correctly only
 *    because its series ascends monotonically — the first point is also the
 *    lowest, so the footer lands right beneath it. Leg Press came back
 *    105 / 105 / 95 / 105: the line starts at the TOP of the plot band, and the
 *    footer sat 78px below it at the bottom-left, reading as though the lift had
 *    started low.
 *
 * These are the two places this card deliberately departs from the mockup.
 * Positions are percentages of the viewBox, so they stay pinned to the data
 * through every resize.
 */
const LABEL_GAP_PX = 4

/**
 * `progression` — the top set for one movement over recent weeks.
 *
 * Inline SVG rather than Recharts: `chart-primitives` exposes a palette, grid
 * colours and the screen-reader table, but no line chart, and a
 * ResponsiveContainer per chat bubble would mount a ResizeObserver per card for
 * a hundred-pixel sparkline. Colours come from CHART_PALETTE/CHART_GRID so this
 * line is the same amber as every other chart in the app.
 *
 * Static by design. This card lands in a chat transcript that already animates
 * on arrival (`ArtifactList`'s motion.div), and a sparkline that redrew or pulsed
 * on its own would keep pulling the eye back to a message the reader has
 * finished with.
 */
export function ProgressionArtifactCard({ data }: { data: ProgressionArtifactData }) {
  const { formatLoad, formatLoadParts, load } = useUnits()
  const points = data.points

  // One point is a dot, not a progression. The backend already declines to send
  // fewer than two (_MIN_POINTS in resolvers/progression.py); a tolerant client
  // must still not divide by zero if that ever changes.
  if (points.length < 2) return null

  // `data.unit` is NOT always a load: `_progression_series`
  // (api/coach/artifacts/resolvers/progression.py) sends `"kg"` for a
  // weight-scored movement but `"s"` for a hold's best-duration series --
  // and the existing "honours a unit other than kg" test below pins a third,
  // arbitrary value passing straight through unconverted. Only the `"kg"`
  // case is a load with a user unit preference to honor; converting a
  // duration through the load formatter would relabel a hold time as
  // pounds, which is worse than the bug this fixes.
  const isLoad = data.unit === 'kg'
  const displayUnit = isLoad ? load : data.unit

  const values = points.map((point) => point.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min

  const xAt = (index: number) =>
    PAD_LEFT + (index * (VB_W - PAD_LEFT - PAD_RIGHT)) / (points.length - 1)
  // A flat series has no span to scale against — draw it along the mid guide.
  const yAt = (value: number) =>
    span === 0 ? MID_Y : PLOT_BOTTOM - ((value - min) / span) * (PLOT_BOTTOM - PLOT_TOP)

  const path = points
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'}${xAt(index).toFixed(1)} ${yAt(point.value).toFixed(1)}`,
    )
    .join(' ')

  // Geometry (position, equality) runs on the RAW backend values -- a linear
  // unit conversion changes what a value reads as, never its relative
  // position or whether two values are equal, so plotting needs no
  // conversion at all. Only what gets DISPLAYED as text below is converted.
  const first = values[0]
  const last = values[values.length - 1]
  const firstY = yAt(first)
  const lastX = xAt(points.length - 1)
  const lastY = yAt(last)

  const displayFirst = isLoad ? formatLoadParts(first).value : String(first)
  const displayLast = isLoad ? formatLoadParts(last).value : String(last)

  // A lift that ends where it began is common — a deload week, or a top set
  // regained. Two identical numbers on one line is noise, and "105 to 105 kg"
  // reads as a bug rather than as a fact, so the round trip gets its own wording
  // and only the amber head label.
  const heldSteady = first === last
  const range = heldSteady
    ? `holding at ${displayLast} ${displayUnit}`
    : `${displayFirst} to ${displayLast} ${displayUnit}`

  /** A value label, anchored to its own point as a percentage of the viewBox. */
  const labelStyle = (x: number, y: number, align: 'left' | 'right'): CSSProperties => ({
    position: 'absolute',
    left: `${(x / VB_W) * 100}%`,
    top: `${(y / VB_H) * 100}%`,
    // Sits the text just above its point. `right` also pulls it clear of the 4px
    // dot it would otherwise sit on top of.
    transform:
      align === 'left'
        ? `translate(0, calc(-100% - ${LABEL_GAP_PX}px))`
        : `translate(calc(-100% - ${LABEL_GAP_PX + 6}px), calc(-100% - ${LABEL_GAP_PX}px))`,
    fontFamily: 'var(--font-mono)',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  })

  return (
    <ArtifactCard eyebrow={data.label} meta={displayUnit} cardType="progression">
      <div style={{ position: 'relative' }}>
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          role="img"
          aria-label={`${data.exercise} top set, ${range} over ${points.length} sessions`}
          style={{ display: 'block', width: '100%', height: 'auto' }}
        >
          <line
            x1="0"
            y1={BASELINE_Y}
            x2={VB_W}
            y2={BASELINE_Y}
            stroke={CHART_GRID.stroke}
            strokeWidth="1"
          />
          <line x1="0" y1={MID_Y} x2={VB_W} y2={MID_Y} stroke={MID_STROKE} strokeWidth="1" />
          <path
            d={path}
            fill="none"
            stroke={CHART_PALETTE.amber}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx={lastX} cy={lastY} r="4" fill={CHART_PALETTE.amber} />
        </svg>

        {/* Both labels are aria-hidden: the chart's aria-label already reads the
            range and SrChartTable carries every point, so exposing these would
            announce two bare numbers in between. */}
        {heldSteady ? null : (
          <span
            data-value-label="first"
            aria-hidden="true"
            style={{
              ...labelStyle(PAD_LEFT, firstY, 'left'),
              fontSize: 9,
              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
              color: 'rgba(245, 237, 225, 0.45)',
            }}
          >
            {displayFirst}
          </span>
        )}
        <span
          data-value-label="last"
          aria-hidden="true"
          style={{ ...labelStyle(lastX, lastY, 'right'), fontSize: 10, color: CHART_PALETTE.amber }}
        >
          {displayLast}
        </span>
      </div>

      <SrChartTable
        caption={`${data.exercise} top set (${displayUnit})`}
        columns={['Date', `Top set (${displayUnit})`]}
        rows={points.map((point) => [point.date, isLoad ? formatLoad(point.value) : point.value])}
        limit={24}
      />
    </ArtifactCard>
  )
}
